import { getTeamNameLabel, openProcessedFile } from '@/helpers/datasetsHelper';
import {
  EXPORTS,
  MY_LIBRARY,
  ROUTE_HOME,
  SHARED_WITH_ME,
  TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
  TOAST_TYPE_ERROR,
  userTeamNameKey,
} from '@/Utils/constants';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  authenticationSliceName,
  datasetSliceName,
  sharedSliceName,
  userDataSliceName,
} from '../../../redux/constants';
import {
  setCurrentFile,
  setExportsFiles,
  setFileSizeProperties,
  setHomeFiles,
  setLibraryFiles,
  setLibraryFolderStructureByName,
  setLibrarySharedWithMe,
  setLibrarySharedWithTeam,
  setProcessingFiles,
} from '../../../redux/reducers/datasetSlice';
import getColumnsCount from '../../../Utils/getColumnsCount';
import sortColumnData from '../../../Utils/sortColumnData';
import {
  WEBSOCKET_CATEGORY_EXPORTS,
  WEBSOCKET_CATEGORY_HOME,
  WEBSOCKET_CATEGORY_LIBRARY,
  WEBSOCKET_CATEGORY_SHARED_WITH_ME,
  WEBSOCKET_CATEGORY_SHARED_WITH_TEAM,
  WEBSOCKET_CATEGORY_FILE,
  useWebSocketContext,
} from './WebSocketContext';
import { Mixpanel } from '@/Mixpanel';

const refreshLibraryFolderStructure = (
  currentDirHandle,
  currentDatasetFiles,
  currentDatasetFolderStructure,
  topLevelParentDirectory
) => {
  const folderStructure = JSON.parse(JSON.stringify(currentDatasetFolderStructure));

  function updateChildren(metadata) {
    if (currentDirHandle === metadata?.FileUuid) {
      return currentDatasetFiles?.map((e) => ({
        ...e.metadata,
        Children: [],
      }));
    } else if (metadata.Children?.length > 0) {
      return metadata.Children.map((child) => ({
        ...child,
        Children: updateChildren(child),
      }));
    } else {
      return metadata.Children;
    }
  }

  if (!currentDirHandle || currentDirHandle === '') {
    return [
      {
        FileName: topLevelParentDirectory,
        FileUuid: 0,
        Children: currentDatasetFiles?.map((e) => ({
          ...e.metadata,
          Children: [],
        })),
      },
    ];
  } else {
    return folderStructure.map((e) => {
      const updatedNode = { ...e, Children: updateChildren(e) };
      return updatedNode;
    });
  }
};

const getLoadedStructure = (sortData, libraryStructure = []) => {
  try {
    const modifiedSharedWithMeFiles = libraryStructure.map((e) => ({
      ...e,
      FileColumns: getColumnsCount(e.metadata),
    }));

    return sortColumnData(modifiedSharedWithMeFiles, sortData.direction, sortData.columnName);
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const getWebSocketCategory = (currentDataset) => {
  if (currentDataset === MY_LIBRARY) return WEBSOCKET_CATEGORY_LIBRARY;
  if (currentDataset === SHARED_WITH_ME) return WEBSOCKET_CATEGORY_SHARED_WITH_ME;
  if (currentDataset === MY_LIBRARY && location.pathname.includes(ROUTE_HOME))
    return WEBSOCKET_CATEGORY_HOME;
  if (location.pathname.includes(EXPORTS)) return WEBSOCKET_CATEGORY_EXPORTS;
  if (currentDataset.includes('Workspace')) return WEBSOCKET_CATEGORY_SHARED_WITH_TEAM;
  throw new Error(`Category Doesn't Exist: ${currentDataset}`);
};

export const WebSocketContainer = () => {
  const dispatch = useDispatch();
  const { lastMessage, spaceUsedLastMessage, pendingReplies, setPendingReplies } =
    useWebSocketContext();
  const spaceUsed = useRef();
  const initBuffer = useRef([]);
  const {
    sortingData,
    currentDir,
    folderStructure,
    sharedWithMeFolderStructure,
    sharedWithMyTeamFolderStructure,
    processingFiles,
  } = useSelector((state) => state[datasetSliceName]);
  const { user } = useSelector((state) => state[userDataSliceName]);
  const { isRanByCypress } = useSelector((state) => state[sharedSliceName]);
  const { auth0IdToken } = useSelector((state) => state[authenticationSliceName]);
  const { maxStorageSize } = user?.userProperties || {};

  useEffect(() => {
    if ((lastMessage !== null && auth0IdToken) || (isRanByCypress && lastMessage !== null)) {
      try {
        const files = JSON.parse(lastMessage?.data);

        switch (files.category) {
          case WEBSOCKET_CATEGORY_LIBRARY: {
            const currentSpaceUsed = spaceUsed.current?.space_used;
            if (currentSpaceUsed !== null && currentSpaceUsed !== undefined) {
              dispatch(setFileSizeProperties({ spaceUsed: currentSpaceUsed, maxStorageSize }));
            }

            const loadedStructure = getLoadedStructure(sortingData, files.files || []);
            dispatch(setLibraryFiles(loadedStructure));
            dispatch(
              setLibraryFolderStructureByName({
                name: 'folderStructure',
                value: refreshLibraryFolderStructure(
                  currentDir,
                  loadedStructure,
                  folderStructure,
                  MY_LIBRARY
                ),
              })
            );

            const res = openProcessedFile(processingFiles, loadedStructure);
            dispatch(setProcessingFiles(res));

            break;
          }
          case WEBSOCKET_CATEGORY_SHARED_WITH_ME: {
            const loadedStructure = getLoadedStructure(sortingData, files.shared_files);
            dispatch(setLibrarySharedWithMe(loadedStructure));

            dispatch(
              setLibraryFolderStructureByName({
                name: 'sharedWithMeFolderStructure',
                value: refreshLibraryFolderStructure(
                  currentDir,
                  loadedStructure,
                  sharedWithMeFolderStructure,
                  SHARED_WITH_ME
                ),
              })
            );

            break;
          }
          case WEBSOCKET_CATEGORY_SHARED_WITH_TEAM: {
            const loadedStructure = getLoadedStructure(sortingData, files.shared_files);
            dispatch(setLibrarySharedWithTeam(loadedStructure));

            dispatch(
              setLibraryFolderStructureByName({
                name: 'sharedWithMyTeamFolderStructure',
                value: refreshLibraryFolderStructure(
                  currentDir,
                  loadedStructure,
                  sharedWithMyTeamFolderStructure,
                  getTeamNameLabel(user[userTeamNameKey])
                ),
              })
            );

            break;
          }
          case WEBSOCKET_CATEGORY_HOME: {
            const currentSpaceUsed = spaceUsed.current?.space_used;
            if (currentSpaceUsed !== null && currentSpaceUsed !== undefined) {
              dispatch(setFileSizeProperties({ spaceUsed: currentSpaceUsed, maxStorageSize }));
            }
            const loadedStructure = getLoadedStructure(sortingData, files.home);
            dispatch(setHomeFiles(loadedStructure));

            break;
          }
          case WEBSOCKET_CATEGORY_EXPORTS: {
            const loadedStructure = getLoadedStructure(sortingData, files.exports);
            dispatch(setExportsFiles(loadedStructure));
            break;
          }
          case WEBSOCKET_CATEGORY_FILE: {
            const [file] = files.files || [];
            if (!file) {
              console.error('No file found.');
            } else {
              dispatch(setCurrentFile({ currentFile: file, user: user }));
            }
            break;
          }

          default:
            console.error('library websocket: missing "category"');
        }
        // handle replies to sent messages
        const parsedMessage = JSON.parse(lastMessage.data);
        if (parsedMessage.id && pendingReplies.has(parsedMessage.id)) {
          const { resolve, timeout } = pendingReplies.get(parsedMessage.id);
          clearTimeout(timeout);
          resolve(parsedMessage);
          setPendingReplies((prev) => {
            const newMap = new Map(prev);
            newMap.delete(parsedMessage.id);
            return newMap;
          });
        }
      } catch (error) {
        console.error('WebSocket message parsing error:', error);
      }
    }
  }, [lastMessage, auth0IdToken, isRanByCypress, pendingReplies]);

  // Buffer the first two space-used readings so we can filter out any “handshake” zeros.
  // If both of the first two readings are zero, we accept zero as the true state.
  // Otherwise we drop any zero until after the warm-up phase, then accept all values.
  useEffect(() => {
    try {
      if (
        (spaceUsedLastMessage !== null && auth0IdToken) ||
        (isRanByCypress && spaceUsedLastMessage !== null)
      ) {
        const { space_used } = JSON.parse(spaceUsedLastMessage.data);
        // first two readings: decide if zero is legit
        if (initBuffer.current.length < 2) {
          initBuffer.current.push(space_used);
          if (initBuffer.current.length === 2) {
            const [first, second] = initBuffer.current;
            if (first === 0 && second === 0) {
              // both zero: accept zero
              spaceUsed.current = { space_used: 0 };
            } else {
              // at least one non-zero: pick the non-zero value
              const valid = second !== 0 ? second : first;
              spaceUsed.current = { space_used: valid };
            }
          }
        } else {
          // after two messages, always accept—even zero
          spaceUsed.current = { space_used };
        }
      }
    } catch (error) {
      Mixpanel.track('Error loading user storage', {
        errorDetails: {
          errorMessage: error?.message || error?.Message,
          errorStack: error?.stack ? error?.stack.substring(0, 500) : 'No stack trace', // Truncate for brevity
          fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
        },
      });
      console.error('Error loading user storage:', error);
    }
  }, [spaceUsedLastMessage, auth0IdToken, isRanByCypress]);

  return null;
};
